/**
 * @file One navigation grid per arena, shared by every AI system.
 *
 * Both the melee FSM ({@link ../systems/EnemyAISystem.js}) and the ranged
 * template ({@link ../systems/EnemyRangedAISystem.js}) navigate the same
 * dungeon. Rasterising it twice would double the memory and CPU spent on each
 * floor change and — worse — let the two grids drift apart if their parameters
 * ever diverged. This module is the single seam through which either system
 * obtains the grid, so there is exactly one instance per arena.
 *
 * A {@link DungeonArena} owns and caches its own grid via `getNavGrid()`; both
 * systems reach it through here. Bare arenas (test stubs, non-dungeon levels)
 * that only publish `blockers`/`bounds` get a grid built once and cached on the
 * arena object itself, so the two systems still share a single instance.
 */

import { NavGrid } from './NavGrid.js';

/** Cell size of the navigation grid, in world units. Shared so both systems agree. */
export const NAV_CELL_SIZE = 0.5;

/**
 * A* runs a single AI system may start per frame. Requests over budget are
 * retried on a later frame rather than dropped, so a crowded arena degrades in
 * latency instead of frame time (A* itself is already capped per call by
 * `MAX_EXPANSIONS` in {@link ./NavGrid.js}).
 *
 * The melee and ranged systems each keep their own counter of this size rather
 * than one shared pool: they are scheduled independently and neither reliably
 * runs first (either may have zero agents on a given frame), which makes a
 * single frame-scoped counter awkward to reset exactly once. Two counters of 4
 * still bound the per-frame spike (≤ 8 A* runs) — the property that matters.
 */
export const PATHS_PER_FRAME = 4;

/**
 * Returns the single {@link NavGrid} for an arena, or null when the arena has
 * no geometry to navigate (no bounds yet, or no arena at all).
 *
 * @param {object | null | undefined} arena The active arena (`ctx.arena`).
 * @param {number} [solidHeight] Blocker height, forwarded to cover queries.
 * @returns {NavGrid | null}
 */
export function navGridForArena(arena, solidHeight = 0) {
  if (!arena || !arena.bounds) return null;

  // The real dungeon owns and invalidates its own grid.
  if (typeof arena.getNavGrid === 'function') return arena.getNavGrid();

  // Bare arena: build once and cache on the arena so both systems share it.
  if (arena._sharedNavGrid) return arena._sharedNavGrid;
  arena._sharedNavGrid = new NavGrid({
    bounds: arena.bounds,
    blockers: arena.blockers ?? [],
    cellSize: NAV_CELL_SIZE,
    solidHeight
  });
  return arena._sharedNavGrid;
}
